import { type CSSProperties, useEffect, useId, useMemo, useRef } from 'react';

import classnames from 'classnames';
import { DOMParser as NodeDOMParser } from 'linkedom';

import { ClientOnly } from '@/ClientOnly';

import type { EmbedElementProps } from './Embed.types';

// Extract generated CSS block from processed html attribute
function extractGeneratedCSS(html: string | undefined): string {
  if (!html) return '';

  const cssMatch = html.match(/<!-- BEGIN GENERATED BLOCK -->([\s\S]*?)<!-- END GENERATED BLOCK -->/);
  return cssMatch ? cssMatch[1].trim() : '';
}

// Process script blocks with context-aware sandboxing
// Returns both the processed HTML and whether scripts were found
function processScriptBlocks(
  _id: string,
  htmlOriginal: string,
  isInEditor: boolean,
): { html: string; hasScripts: boolean } {
  // Use linkedom's DOMParser on server, native DOMParser in browser
  const Parser = typeof window !== 'undefined' ? window.DOMParser : NodeDOMParser;
  const parser = new Parser();
  const doc = parser.parseFromString(htmlOriginal, 'text/html');
  const scripts = doc.querySelectorAll('script');

  // Check for ANY elements with on* event handler attributes (onclick, onerror, onload, etc.)
  // This catches XSS attempts like: <img src=x onerror=alert(document.cookie)>
  const elementsWithEventHandlers = Array.from(doc.querySelectorAll('*')).filter((el) => {
    // biome-ignore lint/suspicious/noExplicitAny: linkedom NamedNodeMap compatibility
    return Array.from<Attr>(el.attributes as any).some((attr) => attr.name.toLowerCase().startsWith('on'));
  });

  const hasScripts = scripts.length > 0 || elementsWithEventHandlers.length > 0;

  if (!hasScripts) return { html: htmlOriginal, hasScripts: false };

  // Check for no-iframe directive
  const noIframe = htmlOriginal.match(/<!--\s*beehiiv:no-iframe\s*-->/);
  if (noIframe) {
    // Wrap scripts in template tags (prevents execution in editor)
    scripts.forEach((script) => {
      const template = doc.createElement('template');
      template.setAttribute('data-html-embed', 'true');
      template.innerHTML = script.outerHTML;
      script.replaceWith(template);
    });
    return { html: doc.body.innerHTML.replace(noIframe[0], ''), hasScripts: true };
  }

  // Wrap in iframe
  // Include embed ID for sandboxed iframes (editor) where frameElement access is blocked
  // Published sites can still use event.source.frameElement as fallback
  const iframe = doc.createElement('iframe') as HTMLIFrameElement;

  const preamble = `<html>
    <head>
      <script src="https://unpkg.com/@tailwindcss/browser@4"></script>
      <script>
        function beehiiv_sendHeight(reason) {
          return function () {
            const height = document.body.scrollHeight;
            parent.postMessage({ type: "beehiiv:iframe_height", height, reason, embedId: "${_id}" }, "*");
          }
        }

        document.addEventListener("DOMContentLoaded", beehiiv_sendHeight("DOMContentLoaded"));
        window.addEventListener("resize", beehiiv_sendHeight("resize"));

        // Capture user interactions that could alter content height
        document.addEventListener("input", beehiiv_sendHeight("input"));
        document.addEventListener("change", beehiiv_sendHeight("change"));
      </script>
    </head>
    <body>`;

  const postscript = `
  <script>
    // Observe mutations to detect content changes dynamically
    const observer = new MutationObserver(beehiiv_sendHeight("MutationObserver"));
    try {
      observer.observe(document.body, { childList: true, subtree: true });
    }
    catch (e) {
      console.error(e)
    }
  </script>
  </body></html>`;

  // Use setAttribute to ensure consistent formatting between server (linkedom) and browser
  // Browser normalizes to "width: 100%;" but linkedom doesn't add the space/semicolon
  iframe.setAttribute('style', 'width: 100%;');

  // On server (linkedom), we need to manually HTML-encode the srcdoc content
  // to match what browsers do automatically. This prevents hydration mismatches.
  const srcdocContent = `${preamble}${htmlOriginal}${postscript}`;
  if (typeof window === 'undefined') {
    // Server: Manually HTML-encode to match browser behavior
    const encoded = srcdocContent
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    iframe.setAttribute('srcdoc', encoded);
  } else {
    // Browser: Setting srcdoc property auto-encodes
    iframe.setAttribute('srcdoc', srcdocContent);
  }

  // Apply sandbox based on context
  // Editor: Allow scripts to run for interactive widgets but keep isolated from parent
  // Published site: No sandbox (full access - that's the feature!)
  if (isInEditor) {
    // In editor: sandbox with allow-scripts and allow-same-origin
    // This enables interactive widgets (calendars, forms, etc.) to function properly
    // Note: allow-same-origin means XSS attacks can access cookies/parent document
    // This is an acceptable trade-off until Phase 3 (separate origin isolation)
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
  }

  return { html: iframe.outerHTML, hasScripts: true };
}

export function EmbedElement(props: EmbedElementProps) {
  const { id, element, attributes, className } = props;
  const {
    mobile_hide,
    desktop_hide,
    customAttributes,
    padding,
    backgroundColor,
    borderWidth,
    borderRadius,
    borderStyle,
    borderColor,
    width,
    maxWidth,
    html,
    htmlOriginal,
  } = element.attrs || {};
  const ref = useRef<HTMLDivElement>(null);
  const reactId = useId();
  // Use provided id or generate stable one with useId
  const embedId = id || reactId;
  const noIframe = htmlOriginal?.match(/<!--\s*beehiiv:no-iframe\s*-->/);

  // Extract generated CSS (cached from save time)
  const generatedCSS = useMemo(() => extractGeneratedCSS(html), [html]);

  // Detect if content needs iframe wrapping (has scripts and no beehiiv:no-iframe)
  const needsIframeWrap = useMemo(() => {
    const htmlToCheck = htmlOriginal || html || '';
    if (!htmlToCheck) return false;

    // Check for scripts
    const hasScripts = /<script/i.test(htmlToCheck) || /\son\w+\s*=/i.test(htmlToCheck);
    const hasNoIframe = /<!--\s*beehiiv:no-iframe\s*-->/.test(htmlToCheck);

    return hasScripts && !hasNoIframe;
  }, [htmlOriginal, html]);

  // Process htmlOriginal at render time
  //
  // Hydration Strategy:
  // - Always process HTML on both server and client (generates consistent output)
  // - For iframe-wrapped content: Use <ClientOnly> wrapper to delay rendering until after hydration
  //   * Server: Renders empty placeholder
  //   * Client first render: Matches server (empty placeholder) - NO HYDRATION ERROR
  //   * Client after hydration: useHydrated() triggers re-render with full iframe content
  // - For simple HTML (no scripts): Render normally on server and client (good for SEO)
  //
  // This approach avoids hydration mismatches caused by:
  // - linkedom (server) vs browser DOM differences in attribute ordering
  // - Different HTML encoding behavior between server and client
  // - Unpredictable user HTML that can't be consistently processed
  const processedHTML = useMemo(() => {
    // If htmlOriginal is empty, fall back to html attribute (for backwards compatibility)
    const htmlToProcess = htmlOriginal || html || '';
    if (!htmlToProcess) return '';

    // During SSR, process as published site (no sandbox)
    const { html: processed } = processScriptBlocks(embedId, htmlToProcess, false);
    const finalHTML = generatedCSS ? `${generatedCSS}\n${processed}` : processed;

    return finalHTML;
  }, [embedId, htmlOriginal, html, generatedCSS, needsIframeWrap]);

  useEffect(() => {
    if (ref.current) {
      const isInEditor = ref.current.closest('[data-node-view-wrapper]') !== null;

      // Use htmlOriginal if present, otherwise fall back to html (for backwards compatibility)
      const htmlToProcess = htmlOriginal || html || '';

      if (isInEditor) {
        // Check if HTML has scripts/event handlers
        const { html: processed, hasScripts } = processScriptBlocks(embedId, htmlToProcess, true);

        // Only show placeholders for no-iframe content (which has no sandbox protection)
        // Regular script content will render in sandboxed iframe
        if (hasScripts && noIframe) {
          // Check if the ORIGINAL HTML has visible content (before iframe wrapping)
          // Use DOMParser instead of innerHTML to avoid executing event handlers
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlToProcess, 'text/html');

          // Remove all script tags before checking for visible text
          // (script contents would otherwise appear as innerText)
          doc.querySelectorAll('script').forEach((script) => {
            script.remove();
          });

          const visibleText = doc.body.innerText?.trim() || '';

          if (visibleText) {
            // Has visible content - show the text with a notice about scripts
            ref.current.innerHTML = `<div class="border-2 border-blue-300 border-dotted bg-blue-50 text-black p-4">
              <div class="text-xs text-blue-600 font-semibold mb-2">⚠️ Scripts detected and not executed in editor</div>
              <div class="text-sm">${visibleText}</div>
            </div>`;
          } else {
            // Script-only, no visible content - show placeholder
            ref.current.innerHTML = `<div class="border-2 border-red-300 border-dotted text-center bg-white text-black p-4">Your HTML embed appears to be <code class="font-bold">&lt;script&gt;</code> only.
            Scripts are not executed in the editor, but will render when published. You can also Preview to verify your HTML embed.</div>`;
          }
        } else if (hasScripts && !noIframe) {
          // Regular sandboxed iframe - render with sandbox attribute
          const finalHTML = generatedCSS ? `${generatedCSS}\n${processed}` : processed;
          ref.current.innerHTML = finalHTML;
        } else if (!hasScripts) {
          // No scripts - render normally
          const finalHTML = generatedCSS ? `${generatedCSS}\n${processed}` : processed;
          ref.current.innerHTML = finalHTML;
        }
        // If hasScripts && !noIframe: do nothing, let dangerouslySetInnerHTML render sandboxed iframe
        return;
      }

      // Published site (not in editor)
      // Only handle noIframe case - regular iframes use dangerouslySetInnerHTML
      if (noIframe && !ref.current.dataset.rendered) {
        // Remount scripts for noIframe embeds (they need manual script execution)
        const finalHTML = generatedCSS ? `${generatedCSS}\n${htmlToProcess}` : htmlToProcess;
        ref.current.innerHTML = finalHTML;
        remountScripts(embedId);
        ref.current.dataset.rendered = 'true';
      }
    }
  }, [embedId, htmlOriginal, html, generatedCSS, noIframe]);

  // Use dangerouslySetInnerHTML for initial render to prevent hydration mismatch
  // useEffect will update innerHTML ONLY for editor context (where we need sandboxing)
  // Published sites render once via dangerouslySetInnerHTML and stay that way
  // no-iframe blocks are client-only (no SSR) to avoid hydration issues

  const htmlProp = noIframe ? undefined : { __html: processedHTML };

  const EmbedDiv = (
    <div
      id={embedId}
      ref={ref}
      data-embed="true"
      {...customAttributes}
      className={classnames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        customAttributes?.className,
      )}
      style={
        {
          position: 'relative',
          padding: padding,
          backgroundColor: backgroundColor,
          borderWidth: borderWidth,
          borderRadius: borderRadius,
          borderStyle: borderStyle,
          borderColor: borderColor,
          width: width,
          maxWidth: maxWidth,
          whiteSpace: 'normal',
        } as CSSProperties
      }
      {...attributes}
      dangerouslySetInnerHTML={htmlProp}
    />
  );

  if (needsIframeWrap) {
    return <ClientOnly>{() => EmbedDiv}</ClientOnly>;
  }
  return EmbedDiv;
}

// This function remounts any <script> tags are part of the HTML embed.
// The browser will not execute <script> tags that are added via innerHTML,
// on the client. And we can't SSR them either since this causes hydration errors.
const remountScripts = (id: string) => {
  const embed = document.getElementById(id);
  if (!embed) {
    return;
  }
  const scripts = Array.from(embed.querySelectorAll('script')) as HTMLScriptElement[];
  let i = 0;
  for (const script of scripts) {
    const attributes = Array.from(script.attributes);

    // create a new script tag thats a copy of the old one
    const newScript = document.createElement('script');
    attributes.forEach((attribute) => {
      newScript.setAttribute(attribute.name, attribute.value);
    });
    newScript.innerHTML = script.innerHTML;
    newScript.id = `html-embed-script-${id}-${i++}`;
    const existingScript = document.getElementById(newScript.id);
    if (existingScript) {
      // remove the existing script to avoid duplicates
      existingScript.remove();
    }
    embed.appendChild(newScript);
  }
};
